/**
 * User switch (#1336): the same token now answers as another user id.
 *
 * Its own module, not `registry.ts`: it reads and writes the auth cache
 * (`authData`), and `authData` imports the stores that register themselves in
 * the registry. Keeping the registry a leaf avoids a cycle that reads the
 * registry before it exists.
 */

import { queryClient } from '@/api/queryClient';
import { setCurrentUser } from '@/auth/authData';
import { getToken } from '@/auth/session';
import { contactMethodKey } from '@/utils/contactMethodUtils';
import type { User } from '@/types';
import { runUserSwitchResets } from './registry';


/**
 * Query keys whose rows belong to ONE user and must not render after the
 * switch: they are reset (data dropped, in-flight fetch cancelled, refetched
 * while mounted). Everything else that is mounted is refreshed in place and
 * keeps its data until the answer lands, so no screen drops to a loading
 * state and nothing unmounts. Each entry has a defensive test
 * (`test_adoptSurvivingUser_relies_on_per_user_keys_<key>`) that proves the
 * real feature still uses the key; a feature that renames its key must update
 * this list, not the test.
 *
 * Deliberately NOT listed (lead decision, plan "library lists refreshed"):
 * `['viewer','courses',…]` and `['viewer','certificates',…]`. They keep their
 * data and refresh in place like every other active query, so no screen drops
 * to a loading state; `test_1336_library_lists_are_refreshed_in_place_on_switch`
 * pins that.
 */
export const PER_USER_RESET_KEYS: readonly (readonly string[])[] = [
  ['viewer', 'inbox'],
  ['stream'],
  ['contactFilters'],
];

/** Keyed by share; the survivor's GET returns the same person's submission. Never reset or removed. */
const KEPT_KEYS: readonly (readonly string[])[] = [['viewData']];

function keyStartsWith(queryKey: readonly unknown[], prefix: readonly string[]): boolean {
  return prefix.every((part, i) => queryKey[i] === part);
}

function isAuthMeKey(queryKey: readonly unknown[]): boolean {
  return keyStartsWith(queryKey, ['auth', 'me']);
}

const PENDING_METHODS_PREFIX = 'ving_pending_contact_methods_';

function movePendingContactMethods(oldId: number, newId: number): void {
  try {
    const fromKey = `${PENDING_METHODS_PREFIX}${oldId}`;
    const toKey = `${PENDING_METHODS_PREFIX}${newId}`;
    const read = (k: string): { value: string; type: string }[] => {
      const raw = localStorage.getItem(k);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as { value: string; type: string }[]) : [];
    };
    const from = read(fromKey);
    if (from.length === 0) return;
    const merged = [...read(toKey)];
    const seen = new Set(merged.map((m) => contactMethodKey(m.value)));
    for (const m of from) {
      const key = contactMethodKey(m.value);
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(m);
    }
    localStorage.setItem(toKey, JSON.stringify(merged));
    localStorage.removeItem(fromKey);
  } catch {
    // storage unavailable or corrupt: pending methods are a convenience
  }
}

/**
 * The session's token now authenticates as `next`, a different user id than
 * the one cached (the person's second login was merged into this one,
 * vingapi#1858). The person keeps working: this NEVER ends the session. It
 * does not touch the token, clear the query cache, or navigate; nothing
 * mounted unmounts.
 *
 * Runs the `onUserSwitch` resets (the staff store DOES reset here: its
 * `orgOverride` belongs to the login that was folded), drops queries nobody is
 * watching, resets the per-user lists, refreshes every other mounted query in
 * place, carries `ving_pending_contact_methods_<oldId>` over to the new id,
 * and writes `next` (with the live token, which /me omits) to the cache and
 * the seed. The auth store mirrors the cache.
 */
export function adoptSurvivingUser(next: User, previousId?: number): void {
  runUserSwitchResets();

  if (previousId !== undefined && previousId !== next.id) {
    movePendingContactMethods(previousId, next.id);
  }

  const cache = queryClient.getQueryCache();
  for (const query of cache.getAll()) {
    const key = query.queryKey;
    if (isAuthMeKey(key) || KEPT_KEYS.some((k) => keyStartsWith(key, k))) continue;
    if (query.getObserversCount() === 0) cache.remove(query);
  }

  const isPerUser = (key: readonly unknown[]) => PER_USER_RESET_KEYS.some((k) => keyStartsWith(key, k));
  void queryClient.resetQueries({ predicate: (q) => isPerUser(q.queryKey) });
  void queryClient.invalidateQueries({
    predicate: (q) => !isAuthMeKey(q.queryKey) && !isPerUser(q.queryKey),
    refetchType: 'active',
  });

  const token = getToken();
  setCurrentUser((token !== undefined ? { ...next, token } : next) as User);
}
